import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/dashboard/Sidebar';

const TABS = ['Cost Codes', 'Vendors', 'Projects'];

export default function Mapping() {
  const [tab, setTab] = useState(0);
  const [costCodes, setCostCodes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    loadMappings();
  }, [tab]);

  const loadMappings = async () => {
    setLoading(true);
    try {
      if (tab === 0) {
        const res = await axios.get('/api/mapping/cost-codes');
        setCostCodes(res.data);
      } else if (tab === 1) {
        const res = await axios.get('/api/mapping/vendors');
        setVendors(res.data);
      } else {
        const res = await axios.get('/api/mapping/projects');
        setProjects(res.data);
      }
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const runAutoMap = async () => {
    if (tab === 0 && !projectId) {
      setAlert({ type: 'warning', msg: 'Enter a Procore Project ID to auto-map cost codes' });
      return;
    }
    setAutoMapping(true);
    try {
      if (tab === 0) {
        const res = await axios.post('/api/mapping/cost-codes/auto-map', { projectId });
        setAlert({ type: 'success', msg: `AI mapped ${res.data.count} cost codes with confidence scores` });
        loadMappings();
      } else if (tab === 1) {
        const res = await axios.post('/api/mapping/vendors/auto-map');
        setAlert({ type: 'success', msg: `AI matched ${res.data.count} vendors` });
        loadMappings();
      }
    } catch (err) {
      setAlert({ type: 'error', msg: err.response?.data?.error || 'Auto-map failed' });
    } finally {
      setAutoMapping(false);
    }
  };

  const currentData = [costCodes, vendors, projects][tab];

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h2 className="page-title">Mappings</h2>
              <p className="page-sub">AI-powered matching between Procore and QuickBooks data</p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {tab === 0 && (
                <input
                  className="form-input"
                  style={{ width: 180 }}
                  placeholder="Procore Project ID"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                />
              )}
              {tab !== 2 && (
                <button className="btn btn-primary" onClick={runAutoMap} disabled={autoMapping}>
                  {autoMapping
                    ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Mapping...</>
                    : '✦ AI Auto-Map'}
                </button>
              )}
            </div>
          </div>
        </div>

        {alert && (
          <div className={`alert alert-${alert.type}`} style={{ marginBottom: 16 }}>
            {alert.msg}
            <button onClick={() => setAlert(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        <div className="tabs">
          {TABS.map((t, i) => (
            <button key={t} className={`tab ${tab === i ? 'active' : ''}`} onClick={() => setTab(i)}>{t}</button>
          ))}
        </div>

        <div className="panel">
          {loading ? (
            <div className="loading"><div className="spinner" /> Loading mappings...</div>
          ) : currentData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">⇄</div>
              <h3 className="empty-state-title">No mappings yet</h3>
              <p className="empty-state-desc">
                {tab === 0 ? 'Enter a Project ID and run AI Auto-Map to generate cost code mappings' :
                 tab === 1 ? 'Run AI Auto-Map to match vendors between Procore and QuickBooks' :
                 'Run a Project Sync from the Dashboard to create project mappings'}
              </p>
            </div>
          ) : (
            <div className="table-wrap">
              {tab === 0 && <CostCodeTable rows={costCodes} onRefresh={loadMappings} />}
              {tab === 1 && <VendorTable rows={vendors} />}
              {tab === 2 && <ProjectTable rows={projects} />}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function CostCodeTable({ rows, onRefresh }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Procore Cost Code</th>
          <th>QB Item</th>
          <th>QB Account</th>
          <th>Confidence</th>
          <th>Mapped By</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>
              <div style={{ fontWeight: 500 }}>{row.procore_cost_code}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.procore_cost_code_name}</div>
            </td>
            <td>{row.qb_item_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
            <td>{row.qb_account_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
            <td>
              {row.confidence_score != null ? (
                <ConfidenceBar value={row.confidence_score} />
              ) : '—'}
            </td>
            <td>
              <span className={`badge ${row.mapped_by === 'ai' ? 'badge-blue' : 'badge-green'}`}>
                {row.mapped_by === 'ai' ? '✦ AI' : '✎ Manual'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VendorTable({ rows }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Procore Vendor</th>
          <th>QuickBooks Vendor</th>
          <th>Status</th>
          <th>Mapped By</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>{row.procore_vendor_name}</td>
            <td>{row.qb_vendor_name || <span style={{ color: 'var(--text-muted)' }}>Not matched</span>}</td>
            <td><span className={`badge badge-${row.sync_status === 'synced' ? 'green' : 'yellow'}`}>{row.sync_status}</span></td>
            <td><span className={`badge ${row.mapped_by === 'ai' ? 'badge-blue' : 'badge-green'}`}>{row.mapped_by}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProjectTable({ rows }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Procore Project</th>
          <th>QB Customer / Job</th>
          <th>Status</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>{row.procore_project_name}</td>
            <td>{row.qb_customer_name || <span style={{ color: 'var(--text-muted)' }}>Pending</span>}</td>
            <td><span className={`badge badge-${row.sync_status === 'synced' ? 'green' : 'yellow'}`}>{row.sync_status}</span></td>
            <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {new Date(row.created_at).toLocaleDateString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'var(--accent-green)' : pct >= 50 ? 'var(--accent-yellow)' : 'var(--accent-red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span className="badge" style={{ fontSize: 12, color, fontWeight: 600, minWidth: 30 }}>{pct}%</span>
    </div>
  );
}
